import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Spinner } from "@ui/spinner/Spinner"
import { Typo } from "@ui/text/Typo"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { GD } from "src/GD"
import { useCallback, useEffect, useState } from "react"

interface Props {
	userUid: string
	onLoadingChange?: (loading: boolean) => void
}

export const UserBlockReturnPreview = ({ userUid, onLoadingChange }: Props) => {
	const [preview, setPreview] = useState<IUserBlockPreview | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(true)

	const load = useCallback(async (refresh = false) => {
		setLoading(true)
		onLoadingChange?.(true)
		try {
			const response = await GD.REQ_USER_BLOCK_PREVIEW.request({ user_uid: userUid, refresh })
			if (response.error || !response.data) {
				setPreview(null)
				setError(response.error?.message || "Could not load the return estimate.")
			} else {
				setPreview(response.data)
				setError(null)
			}
		} catch {
			setPreview(null)
			setError("Could not load the return estimate.")
		} finally {
			setLoading(false)
			onLoadingChange?.(false)
		}
	}, [onLoadingChange, userUid])

	useEffect(() => { void load(false) }, [load])
	const amount = (value: number) => formatCurrencyWithNumberToSymbolWithNumber(value, preview?.currency || "USD")

	return (
		<Box role='region' aria-label='Return estimate' sx={{ padding: "11px 12px", borderRadius: "9px", backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }}>
			<HBox sx={{ justifyContent: "space-between", gap: "10px", alignItems: "center" }}>
				<Typo sx={{ color: "#991B1B", fontWeight: 800, fontSize: "0.82rem" }}>Estimated return to company</Typo>
				<Button title='Refresh' variant='secondarySmall' disabled={loading} onClick={() => void load(true)} />
			</HBox>
			{loading ? (
				<HBox gap='8px' role='status' sx={{ marginTop: "8px", alignItems: "center" }}><Spinner size={16} /><Typo sx={{ fontSize: "0.8rem" }}>Updating balances...</Typo></HBox>
			) : error ? (
				<Typo sx={{ marginTop: "8px", color: "#991B1B", fontSize: "0.8rem" }}>{error}</Typo>
			) : preview && (
				<VBox gap='7px' sx={{ marginTop: "8px", color: "#7F1D1D", fontSize: "0.8rem" }}>
					{preview.issuers.map(row => (
						<VBox key={row.issuer} gap='2px'>
							<Typo sx={{ fontSize: "inherit" }}>Buyer balances · {row.issuer_label}: {amount(row.buyer_balance)}</Typo>
							<Typo sx={{ fontSize: "inherit" }}>Card balances · {row.issuer_label}: {amount(row.cards_balance)}</Typo>
							{row.cards_residual > 0 && <Typo sx={{ fontSize: "inherit", color: "#9A3412" }}>minus what {row.issuer_label} keeps: −{amount(row.cards_residual)}</Typo>}
						</VBox>
					))}
					<Typo sx={{ fontSize: "inherit", fontWeight: 800, borderTop: "1px solid #FECACA", paddingTop: "8px" }}>{preview.issuers.length && preview.total > 0 ? `Estimated total: ${amount(preview.total)}` : "Nothing to return"}</Typo>
					{preview.warnings.map((warning, index) => <Typo key={index} sx={{ fontSize: "0.76rem", color: "#7F1D1D" }}>{warning}</Typo>)}
				</VBox>
			)}
			<Typo sx={{ marginTop: "9px", color: "#7F1D1D", fontSize: "0.76rem", lineHeight: 1.5 }}>Closing cards is irreversible. The final amount can change before closure completes.</Typo>
		</Box>
	)
}
